import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'font-halyarddisplay text-black inline-flex items-center justify-center font-semibold whitespace-nowrap ring-offset-background transition-colors group-focus:outline-none group-focus:ring-2 group-focus:ring-ring group-focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'relative group rounded-full hover:text-white focus:text-white active:text-white',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'relative group rounded-full hover:text-white active:text-white focus:text-white',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'squiggly-link hover:text-white active:text-white focus:text-white',
        linkInline:
          'squiggly-link-inline hover:text-zinc-550 active:text-zinc-550 focus:text-zinc-550 after:text-zinc-550 before:text-black',
        icon: 'rounded-full bg-blue-500 hover:bg-blue-500 focus:bg-blue-500 active:bg-blue-500 fill-white ease-bounce animate-jello-reverse hover:animate-jello focus:animate-jello',
      },
      size: {
        default: 'text-xl px-5 pt-2 pb-2.5',
        sm: 'text-base px-4 pt-2 pb-2.5',
        lg: 'text-2xl px-7 pt-2 pb-3',
        icon: 'h-9 w-9 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  childClassName?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, childClassName = '', asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    // Determine if the inner wrapper should be used
    const useInnerWrapper = variant === 'default' || variant === 'outline';

    // Render the inner wrapper if the variant is 'default' or 'outline'
    const content = useInnerWrapper ? (
      <>
        <div
          className={`${variant === 'outline' ? 'border border-current bg-transparent' : 'bg-yellow-500'} ease-bounce absolute inset-0 flex items-center overflow-hidden rounded-full transition-all after:absolute after:block after:w-full after:scale-0 after:rounded-full after:bg-blue-950 after:pt-[100%] after:opacity-20 after:transition-all group-hover:scale-[1.05] group-hover:after:scale-100 group-hover:after:opacity-100 group-focus:scale-[1.05] group-focus:after:scale-100 group-focus:after:opacity-100 group-active:scale-[1.05] group-active:after:scale-100 group-active:after:opacity-100 md:group-hover:scale-[1.15] md:group-focus:scale-[1.15] md:group-active:scale-[1.15] ${childClassName}`}
        ></div>
        <span className='relative'>{children}</span>
      </>
    ) : (
      children
    );

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {content}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
